import React, { useCallback, useRef } from "react";
import { Form, Spinner } from "react-bootstrap";

/** 
 * email template:
 * ```
<h2>Magic Link</h2>
<p>Here is the code you need to access your account:</p>
<p>{{ .Token }}</p>
<p>You also may follow this link to sign in on this device:</p>
<p><a href="{{ .ConfirmationURL }}">/sign In</a></p>
 * ```
 *
 * website text:
 * ```
Check your inbox for the code you need to access your account.
 * ```
 */

export const AuthTokenInput = (p: {
  length: number;
  isPending?: boolean;
  onChange?: (token: string) => void;
  onCompleted?: (token: string) => void;
}) => {
  const inputRefs = useRef<HTMLInputElement[]>([]);

  const notify = useCallback(() => {
    const token = inputRefs.current
      .map(ref => ref.value.charAt(0))
      .join("");
    p.onChange?.(token);
    token.length === p.length && p.onCompleted?.(token);
  }, [p.onChange]);

  const onChange = useCallback((i: number) => () => {
    inputRefs.current[
      inputRefs.current[i].value.length
        ? i + 1
        : i - 1
    ]?.focus();

    notify();
  }, [notify])

  const onPaste = useCallback((i: number) => (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedValue = e.clipboardData.getData('Text');
    for (let j = 0; j < inputRefs.current.length; j++) {
      if (pastedValue[j] && inputRefs.current[j + i]) {
        inputRefs.current[j + i].value = pastedValue[j];
      }
    }
    inputRefs.current[Math.min(i + pastedValue.length, inputRefs.current.length) - 1].focus();

    notify();
  }, [notify]);

  const widthPercent = Math.floor(100 / p.length) - 1; // one percent for margin

  return (
    <div className="d-flex justify-content-between">
      {p.isPending ? (
        <Spinner animation="grow" />
      ) : (
        Array.from({ length: p.length }).map((_, i) =>
          <Form.Control
            style={{ width: `${widthPercent}%` }}
            className="text-center"
            key={i}
            ref={(el: HTMLInputElement) => inputRefs.current[i] = el}
            onChange={onChange(i)}
            onPaste={onPaste(i)}
            type="text"
            maxLength={1}
          />
        )
      )}
    </div>
  );
}
